import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2 } from 'lucide-react';
interface LocationData {
  city: string;
  country: string;
  countryCode: string;
}
const NAMES_BY_COUNTRY: Record<string, string[]> = {
  MX: ['Guadalupe', 'Carlos', 'Fernanda', 'José', 'Valentina', 'Miguel', 'Sofía', 'Andrés', 'Camila', 'Roberto'],
  CO: ['Catalina', 'Santiago', 'Daniela', 'Sebastián', 'Laura', 'Alejandro', 'Natalia', 'David', 'Paola', 'Julián'],
  CL: ['Francisca', 'Matías', 'Constanza', 'Tomás', 'Javiera', 'Nicolás', 'Isidora', 'Benjamín', 'Antonia', 'Felipe'],
  PE: ['Milagros', 'Luis', 'Valeria', 'Jorge', 'Lucero', 'César', 'Fiorella', 'Renato', 'Ariana', 'Diego'],
  UY: ['Agustina', 'Facundo', 'Martina', 'Gonzalo', 'Camila', 'Joaquín', 'Valentina', 'Ignacio', 'Florencia', 'Mateo'],
  EC: ['Gabriela', 'Andrés', 'María José', 'Sebastián', 'Daniela', 'Cristian', 'Pamela', 'Kevin', 'Mishell', 'Bryan'],
  DEFAULT: ['Guadalupe', 'Santiago', 'Francisca', 'Milagros', 'Agustina', 'Gabriela', 'Catalina', 'Matías', 'Valeria', 'Facundo']
};
const CITIES_BY_COUNTRY: Record<string, string[]> = {
  MX: ['Ciudad de México', 'Guadalajara', 'Monterrey', 'Puebla', 'Querétaro', 'Mérida', 'Cancún', 'León'],
  CO: ['Bogotá', 'Medellín', 'Cali', 'Barranquilla', 'Cartagena', 'Bucaramanga', 'Pereira', 'Santa Marta'],
  CL: ['Santiago', 'Valparaíso', 'Concepción', 'La Serena', 'Temuco', 'Antofagasta', 'Viña del Mar', 'Rancagua'],
  PE: ['Lima', 'Arequipa', 'Trujillo', 'Cusco', 'Chiclayo', 'Piura', 'Huancayo', 'Iquitos'],
  UY: ['Montevideo', 'Salto', 'Paysandú', 'Las Piedras', 'Rivera', 'Maldonado', 'Tacuarembó', 'Melo'],
  EC: ['Quito', 'Guayaquil', 'Cuenca', 'Ambato', 'Manta', 'Riobamba', 'Loja', 'Machala'],
  DEFAULT: ['Ciudad de México', 'Bogotá', 'Santiago', 'Lima', 'Montevideo', 'Quito', 'Medellín', 'Guadalajara']
};
const COUNTRY_NAMES: Record<string, string> = {
  MX: 'México',
  CO: 'Colombia',
  CL: 'Chile',
  PE: 'Perú',
  UY: 'Uruguay',
  EC: 'Ecuador',
  DEFAULT: 'México'
};
const DEFAULT_COUNTRIES = ['MX', 'CO', 'CL', 'PE', 'UY', 'EC'];
const TIMES = ['2 min', '3 min', '5 min', '6 min', '7 min', '9 min', '12 min', '15 min'];
const COLOR_SCHEMES = [{
  bg: 'bg-gradient-to-r from-green-400 to-emerald-500',
  text: 'text-white',
  subtext: 'text-green-50',
  border: 'border-emerald-400',
  shadow: 'shadow-emerald-500/50'
}, {
  bg: 'bg-gradient-to-r from-blue-500 to-indigo-600',
  text: 'text-white',
  subtext: 'text-blue-100',
  border: 'border-indigo-400',
  shadow: 'shadow-indigo-500/50'
}, {
  bg: 'bg-gradient-to-r from-purple-500 to-pink-500',
  text: 'text-white',
  subtext: 'text-purple-100',
  border: 'border-pink-400',
  shadow: 'shadow-pink-500/50'
}, {
  bg: 'bg-gradient-to-r from-orange-400 to-red-500',
  text: 'text-white',
  subtext: 'text-orange-100',
  border: 'border-red-400',
  shadow: 'shadow-red-500/50'
}, {
  bg: 'bg-gradient-to-r from-yellow-400 to-amber-500',
  text: 'text-gray-900',
  subtext: 'text-gray-800',
  border: 'border-amber-400',
  shadow: 'shadow-amber-500/50'
}, {
  bg: 'bg-gradient-to-r from-cyan-400 to-blue-500',
  text: 'text-white',
  subtext: 'text-cyan-50',
  border: 'border-blue-400',
  shadow: 'shadow-blue-500/50'
}, {
  bg: 'bg-gradient-to-r from-rose-500 to-red-600',
  text: 'text-white',
  subtext: 'text-rose-100',
  border: 'border-red-400',
  shadow: 'shadow-red-500/50'
}, {
  bg: 'bg-gradient-to-r from-teal-400 to-green-500',
  text: 'text-white',
  subtext: 'text-teal-50',
  border: 'border-green-400',
  shadow: 'shadow-green-500/50'
}];
function generateMixedPurchases() {
  const purchases: {
    name: string;
    city: string;
    country: string;
    time: string;
  }[] = [];
  for (let i = 0; i < 20; i++) {
    const countryCode = DEFAULT_COUNTRIES[i % DEFAULT_COUNTRIES.length];
    const names = NAMES_BY_COUNTRY[countryCode];
    const cities = CITIES_BY_COUNTRY[countryCode];
    const countryName = COUNTRY_NAMES[countryCode];
    purchases.push({
      name: names[i % names.length],
      city: cities[i % cities.length],
      country: countryName,
      time: TIMES[i % TIMES.length]
    });
  }
  return purchases;
}
function generatePurchases(countryCode: string, detectedCity?: string) {
  if (countryCode === 'AR' || countryCode === 'DEFAULT' || !NAMES_BY_COUNTRY[countryCode]) {
    return generateMixedPurchases();
  }
  const names = NAMES_BY_COUNTRY[countryCode];
  const cities = CITIES_BY_COUNTRY[countryCode];
  const countryName = COUNTRY_NAMES[countryCode];
  return names.map((name, i) => ({
    name,
    city: i === 0 && detectedCity ? detectedCity : cities[i % cities.length],
    country: countryName,
    time: TIMES[i % TIMES.length]
  }));
}
export function SocialProofPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);
  const [purchases, setPurchases] = useState(generateMixedPurchases());
  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const response = await fetch('https://ip-api.com/json/?fields=city,country,countryCode');
        if (response.ok) {
          const data: LocationData = await response.json();
          if (data.countryCode) {
            setPurchases(generatePurchases(data.countryCode, data.city));
          }
        }
      } catch {
        setPurchases(generateMixedPurchases());
      }
    };
    fetchLocation();
  }, []);
  useEffect(() => {
    if (isDismissed) return;
    const initialTimeout = setTimeout(() => {
      setIsVisible(true);
    }, 5000);
    const cycleInterval = setInterval(() => {
      if (isDismissed) return;
      setIsVisible(false);
      setTimeout(() => {
        if (isDismissed) return;
        setCurrentIndex(prev => (prev + 1) % purchases.length);
        setIsVisible(true);
      }, 2000);
    }, 8000);
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(cycleInterval);
    };
  }, [isDismissed, purchases.length]);
  useEffect(() => {
    if (isVisible && !isDismissed) {
      const hideTimeout = setTimeout(() => {
        setIsVisible(false);
      }, 5000);
      return () => clearTimeout(hideTimeout);
    }
  }, [isVisible, isDismissed]);
  if (isDismissed) return null;
  const currentPurchase = purchases[currentIndex];
  const currentScheme = COLOR_SCHEMES[currentIndex % COLOR_SCHEMES.length];
  return <AnimatePresence data-id="element-201">
      {isVisible && <motion.div initial={{
      opacity: 0,
      y: 40,
      x: -10
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} exit={{
      opacity: 0,
      y: 20,
      x: -10
    }} transition={{
      duration: 0.4
    }} className="fixed bottom-3 left-3 z-50 max-w-[260px] w-full" data-id="element-202">
          <div className={`${currentScheme.bg} rounded-lg shadow-xl ${currentScheme.shadow} border ${currentScheme.border} px-3 py-2.5 flex items-center gap-2.5 relative overflow-hidden`} data-id="element-203">
            <button onClick={() => setIsDismissed(true)} className={`absolute top-0.5 right-0.5 ${currentScheme.text} opacity-60 hover:opacity-100 p-0.5`} data-id="element-204">
              <X size={12} data-id="element-205" />
            </button>

            <div className="flex-shrink-0 relative" data-id="element-206">
              <img src="https://cdn.magicpatterns.com/uploads/3EguwFjBLbx4t4XmZ7SgVL/mockups_economia_.png" alt="Product" className="w-9 h-9 object-cover rounded border border-white/20" data-id="element-207" />
              <div className="absolute -bottom-0.5 -right-0.5 bg-green-500 rounded-full p-px border border-white" data-id="element-208">
                <CheckCircle2 size={8} className="text-white" data-id="element-209" />
              </div>
            </div>

            <div className="flex-1 pr-3 min-w-0" data-id="element-210">
              <p className={`text-xs font-bold ${currentScheme.text} leading-tight truncate`} data-id="element-211">
                {currentPurchase.name} de {currentPurchase.city}
              </p>
              <p className={`text-[10px] ${currentScheme.subtext} leading-tight`} data-id="element-212">
                {currentPurchase.country} · hace{' '}
                <span className="font-bold" data-id="element-213">{currentPurchase.time}</span>
              </p>
              <div className="flex items-center mt-0.5" data-id="element-214">
                <div className="flex" data-id="element-215">
                  {[1, 2, 3, 4, 5].map(i => <span key={i} className="text-[#fbbf24] text-[8px]" data-id="element-216">
                      ★
                    </span>)}
                </div>
                <span className={`text-[8px] ${currentScheme.subtext} ml-1 opacity-70`} data-id="element-217">
                  Verificado
                </span>
              </div>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}